import { useState } from 'react'
import { cn } from '@/lib/utils'
import type { OntologyClass, OntologyDomain } from '@/types/ontology'
import { ChevronDown, ChevronRight } from 'lucide-react'

interface ClassTreeProps {
  classes: OntologyClass[]
  selectedClass: OntologyClass | null
  onSelectClass: (cls: OntologyClass) => void
  filterDomain?: OntologyDomain | null
}

interface DomainGroup {
  domain: OntologyDomain
  label: string
  classes: OntologyClass[]
}

const domainConfig: Record<OntologyDomain, { label: string; dotColor: string; headerBg: string }> = {
  core: { label: 'Core', dotColor: 'bg-amber-500', headerBg: 'bg-amber-100 text-amber-700' },
  market: { label: 'Market', dotColor: 'bg-market', headerBg: 'bg-market/10 text-market' },
  business: { label: 'Business', dotColor: 'bg-business', headerBg: 'bg-business/10 text-business' },
  solution: { label: 'Solution', dotColor: 'bg-solution', headerBg: 'bg-solution/10 text-solution' },
}

export function ClassTree({ classes, selectedClass, onSelectClass, filterDomain }: ClassTreeProps) {
  const [expandedDomains, setExpandedDomains] = useState<Record<OntologyDomain, boolean>>({
    core: true,
    market: true,
    business: true,
    solution: true,
  })

  const toggleDomain = (domain: OntologyDomain) => {
    setExpandedDomains((prev) => ({ ...prev, [domain]: !prev[domain] }))
  }

  // Group classes by domain
  const groupedClasses: DomainGroup[] = (['core', 'market', 'business', 'solution'] as OntologyDomain[])
    .filter((domain) => !filterDomain || filterDomain === domain)
    .map((domain) => ({
      domain,
      label: domainConfig[domain].label,
      classes: classes.filter((c) => c.domain === domain),
    }))
    .filter((group) => group.classes.length > 0)

  return (
    <div className="space-y-4">
      {groupedClasses.map((group) => {
        const config = domainConfig[group.domain]
        const isExpanded = expandedDomains[group.domain]

        return (
          <div key={group.domain}>
            {/* Domain Header */}
            <button
              onClick={() => toggleDomain(group.domain)}
              className={cn(
                'w-full flex items-center gap-2 h-9 px-3 text-sm font-medium rounded-lg mb-1',
                config.headerBg
              )}
            >
              <span className="flex-shrink-0">
                {isExpanded ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </span>
              <span className="truncate">{group.label}</span>
              <span className="ml-auto text-xs opacity-70">
                {group.classes.length}
              </span>
            </button>

            {/* Class List */}
            {isExpanded && (
              <ul className="space-y-0.5">
                {group.classes.map((cls) => (
                  <li key={cls.uri}>
                    <button
                      onClick={() => onSelectClass(cls)}
                      className={cn(
                        'w-full flex items-center gap-3 h-9 px-3 text-sm text-left rounded-lg transition-colors',
                        selectedClass?.uri === cls.uri
                          ? 'bg-warm-100 text-text font-medium'
                          : 'hover:bg-warm-50 text-text-secondary'
                      )}
                    >
                      <span className={cn('w-1.5 h-1.5 rounded-full flex-shrink-0', config.dotColor)} />
                      <span className="font-mono text-[13px] truncate">{cls.localName}</span>
                      {cls.properties.length > 0 && (
                        <span className="ml-auto text-[11px] text-text-muted flex-shrink-0">
                          {cls.properties.length}
                        </span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )
      })}
    </div>
  )
}
